import { startTransition } from 'react';
import {
	useMutation,
	UseMutationOptions,
	UseMutationResult,
	useQuery,
	UseQueryOptions,
	UseQueryResult
} from 'react-query';
import { useAxios } from 'Hooks';
import { queryKey } from 'Constants';
import { api, fetch, toastify } from 'Utils';
import {
	DataList,
	DataQueryParams,
	CommunityPartnerType,
	CommunityPartnerProfile,
	CommunityPartnerPartner,
	CommunityPartnerCommissionLevelMe,
	CommunityPartnerAssociation,
	CommunityPartnerSettings,
	CommunityPartnerArea,
	BusinessPartnerMe,
	CommunityPartnerMe,
	PartnersInsight
} from 'Types';
import axios from 'Adapter';

const config = (options = {}) => {
	return {
		...options,
		staleTime: Infinity
	};
};

export const useCommunityPartnerMe = <T = CommunityPartnerMe>(
	options?: UseQueryOptions<T, any, CommunityPartnerMe>
): UseQueryResult<CommunityPartnerMe, unknown> => {
	return useQuery({
		queryKey: [queryKey.communityPartnerMe],
		queryFn: () => fetch.communityPartnerMe(),
		...config(options)
	});
};

export const useCommunityPartnerCommissionLevelMe = <
	T = CommunityPartnerCommissionLevelMe
>(
	options?: UseQueryOptions<T, any, CommunityPartnerCommissionLevelMe>
): UseQueryResult<CommunityPartnerCommissionLevelMe, unknown> => {
	return useQuery({
		queryKey: [queryKey.commissionLevelMe],
		queryFn: () => fetch.communityPartnerCommissionLevelMe(),
		...config(options)
	});
};

export const useCommunityPartnerProfile = <T = CommunityPartnerProfile>(
	communityPartnerProfileId: string,
	options?: UseQueryOptions<T, any, CommunityPartnerProfile>
): UseQueryResult<CommunityPartnerProfile, unknown> => {
	return useQuery({
		queryKey: [
			queryKey.communityPartnerProfile,
			{ id: communityPartnerProfileId }
		],
		queryFn: () => fetch.communityPartnerProfile(communityPartnerProfileId),
		...config(options)
	});
};

export const updateCommunityPartnerEditProfile: (
	communityPartnerProfileId: string,
	fields: CommunityPartnerProfile,
	onFinal?: () => void,
	onCatch?: () => void
) => Promise<void> = async (
	communityPartnerProfileId,
	fields,
	onFinal,
	onCatch
) => {
	const data = fields;
	await axios
		.put(
			api.updateCommunityPartnerEditProfile(communityPartnerProfileId),
			data
		)
		.then((res) => {
			if (res.status === 200) {
				startTransition(() => {
					toastify('toastify.community-partner-updated', {
						type: 'success'
					});
				});
				onFinal?.();
			}
		})
		.catch(() => onCatch?.());
};

export const useCommunityPartnerArea = <T = DataList<CommunityPartnerArea>>(
	{ pageSize = 500, pageNumber = 1, keyword = '' }: DataQueryParams,
	options?: UseQueryOptions<T, any, DataList<CommunityPartnerArea>>
): UseQueryResult<DataList<CommunityPartnerArea>, unknown> => {
	return useQuery({
		queryKey: [
			queryKey.agentPartnersArea,
			{
				pageSize,
				pageNumber,
				keyword
			}
		],
		queryFn: () =>
			fetch.communityPartnerAreaList(pageSize, pageNumber, keyword),
		...config(options)
	});
};

export const useCommunityPartnerPartners = <
	T = DataList<CommunityPartnerPartner>
>(
	{
		pageSize = 12,
		pageNumber = 1,
		keyword = '',
		status = '',
		registrationStatus = '',
		publishStatus = '',
		subscriptionPlanId = ''
	}: DataQueryParams,
	options?: UseQueryOptions<T, any, DataList<CommunityPartnerPartner>>
): UseQueryResult<DataList<CommunityPartnerPartner>, unknown> => {
	return useQuery({
		queryKey: [
			queryKey.communityPartnerPartners,
			{
				pageSize,
				pageNumber,
				keyword,
				status,
				registrationStatus,
				publishStatus,
				subscriptionPlanId
			}
		],
		queryFn: () =>
			fetch.communityPartnerPartners(
				pageSize,
				pageNumber,
				keyword,
				status,
				registrationStatus,
				publishStatus,
				subscriptionPlanId
			),
		...config(options)
	});
};

export const usePartnerDetails = <T = BusinessPartnerMe>(
	businessPartnerId: string,
	options?: UseQueryOptions<T, any, BusinessPartnerMe>
): UseQueryResult<BusinessPartnerMe> => {
	return useQuery({
		queryKey: [queryKey.partnerDetails, { businessPartnerId }],
		queryFn: () => fetch.partnerDetails(businessPartnerId),
		...config(options)
	});
};

export const useUpdateCommunityPartner = (
	options?: UseQueryOptions<CommunityPartnerMe>
) => {
	const query = useCommunityPartnerMe(options);
	const queryProfile = useCommunityPartnerProfile(
		query?.data?.communityPartnerProfileId as string,
		{
			enabled: !!query?.data?.communityPartnerProfileId
		}
	);
	const agent = query.data;
	const address = agent?.address;
	const { sendRequest, error } = useAxios();

	const updateCommunityPartner: (
		fields: Partial<CommunityPartnerMe>,
		onFinal?: () => void,
		onCatch?: () => void
	) => Promise<void> = async (fields, onFinal, onCatch) => {
		const data = {
			...agent,
			...fields
		};
		await sendRequest(
			api.updateCommunityPartnersMe,
			{ data, method: 'PUT' },
			() => {
				query.refetch();
				queryProfile.refetch();
				startTransition(() => {
					toastify('toastify.community-partner-updated', {
						type: 'success'
					});
				});
				onFinal?.();
			}
		);
		if (error) onCatch?.();
	};

	const updateCommunityPartnerSettings: (
		fields: Partial<CommunityPartnerSettings>,
		onFinal?: () => void,
		onCatch?: () => void
	) => Promise<void> = async (fields, onFinal, onCatch) => {
		const data = {
			...fields
		};
		await sendRequest(
			api.updateCommunityPartnerSettings,
			{ data, method: 'PATCH' },
			() => {
				query.refetch();
				startTransition(() => {
					toastify('toastify.community-partner-updated', {
						type: 'success'
					});
				});
				onFinal?.();
			}
		);
		if (error) onCatch?.();
	};

	return {
		...query,
		agent,
		address,
		updateCommunityPartner,
		updateCommunityPartnerSettings
	};
};

export const useCommunityPartnerTypes = <T = DataList<CommunityPartnerType>>(
	{ pageSize, pageNumber, keyword, active }: DataQueryParams,
	options?: UseQueryOptions<T, any, DataList<CommunityPartnerType>>
): UseQueryResult<DataList<CommunityPartnerType>> => {
	return useQuery({
		queryKey: [
			queryKey.communityPartnerTypes,
			{ pageSize, pageNumber, keyword, active }
		],
		queryFn: () =>
			fetch.communityPartnerTypes(pageSize, pageNumber, keyword, active),
		...config(options)
	});
};

export const useCommunityPartnerAssociation = <
	T = DataList<CommunityPartnerAssociation>
>(
	{ pageSize, pageNumber, keyword, active }: DataQueryParams,
	options?: UseQueryOptions<T, any, DataList<CommunityPartnerAssociation>>
): UseQueryResult<DataList<CommunityPartnerAssociation>> => {
	return useQuery({
		queryKey: [
			queryKey.communityPartnerAssociation,
			{ pageSize, pageNumber, keyword, active }
		],
		queryFn: () =>
			fetch.communityPartnerAssociation(
				pageSize,
				pageNumber,
				keyword,
				active
			),
		...config(options)
	});
};

export const useCPProfileInsight = (
	options?: UseMutationOptions<any, any, { fromDate: string; toDate: string }>
): UseMutationResult<any, any, { fromDate: string; toDate: string }> => {
	return useMutation({
		mutationKey: [queryKey.cpProfileInsight],
		mutationFn: (body: { fromDate: string; toDate: string }) =>
			fetch.cpProfileInsight(body),
		...options
	});
};

export const useCommunityPartnerPartnersInsight = <T = PartnersInsight>(
	options?: UseQueryOptions<T, any, PartnersInsight>
): UseQueryResult<PartnersInsight, unknown> => {
	return useQuery({
		queryKey: [queryKey.partnersInsight],
		queryFn: () => fetch.partnersInsight(),
		...config(options)
	});
};
